import React from 'react'
import dayjs from 'dayjs'
import { SearchOutlined } from '@ant-design/icons'
import jsPDF from 'jspdf'
/**
 * Get column search props for Ant Design tables
 * @returns {object} Column search props
 */
export const getColumnSearchProps = () => {
  return {
    filterDropdown: () => { },
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: () => { },
    onFilterDropdownVisibleChange: () => { }
  }
}

/**
 * Format a date string to the desired format
 * @param {string} dateString - The date string to format
 * @param {string} format - The desired format (default: 'DD-MMM-YYYY')
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, format = 'DD-MMM-YYYY') => {
  if (!dateString) return ''

  const date = new Date(dateString)

  if (isNaN(date.getTime())) return dateString

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const day = date.getDate().toString().padStart(2, '0')
  const month = months[date.getMonth()]
  const year = date.getFullYear()

  return format
    .replace('DD', day)
    .replace('MMM', month)
    .replace('YYYY', year)
}

/**
 * Format a time string to 12-hour format
 * @param {string} timeString - The time string to format (HH:MM)
 * @returns {string} Formatted time string (HH:MMAM/PM)
 */
export const formatTime = (timeString) => {
  if (!timeString) return ''

  const [hours, minutes] = timeString.split(':').map(Number)

  if (isNaN(hours) || isNaN(minutes)) return timeString

  const period = hours >= 12 ? 'PM' : 'AM'
  const hour12 = hours % 12 || 12

  return `${hour12}:${minutes.toString().padStart(2, '0')}${period}`
}

/**
 * Format a currency value with the specified decimal places
 * @param {number} value - The value to format
 * @param {boolean} showDecimals - Whether to show decimal places
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, showDecimals = true) => {
  if (value === null || value === undefined) return ''

  const formatter = new Intl.NumberFormat('en-US', {
    currency: 'PHP',
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0
  })

  return formatter.format(value)
}
/**
/**
 * Export transactions as CSV with enhanced metrics - FIXED VERSION
 * @param {Array} data - Array of transaction objects
 * @param {Object} totals - Enhanced totals object with cash count data
 * @param {string} filename - Filename for the CSV file
 */
export const exportTransactionsAsCSV = (data, totals, filename) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.error('Invalid data format for CSV export')
    return
  }

  try {
    // Get the headers from the first object
    const headers = Object.keys(data[0])

    // Create CSV rows
    const csvRows = []

    // Add the headers
    csvRows.push(headers.join(','))

    // Add the data rows
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header]
        const formattedValue = value === null || value === undefined ? '' : String(value)
        return `"${formattedValue.replace(/"/g, '""')}"`
      })
      csvRows.push(values.join(','))
    }

    // Add summary section - FIXED: Use formatCurrency with decimals (true)
    csvRows.push('') // Empty row
    csvRows.push('"--- SUMMARY ---"')
    csvRows.push(`"Total Cash Out:","${formatCurrency(totals.cashOut, true)}"`) // Changed from false to true
    csvRows.push(`"Total Cash In:","${formatCurrency(totals.cashIn, true)}"`) // Changed from false to true
    csvRows.push(`"Running Balance:","${formatCurrency(totals.runningBalance, true)}"`) // Changed from false to true
    csvRows.push(`"System Cash on Hand:","${formatCurrency(totals.systemCashOnHand, true)}"`) // Changed from false to true
    csvRows.push(`"Actual Cash on Hand:","${formatCurrency(totals.actualCashOnHand, true)}"`) // Changed from false to true

    const discrepancyLabel = totals.discrepancy > 0 ? 'Surplus' :
      totals.discrepancy < 0 ? 'Discrepancy' : 'Balanced'
    const discrepancyAmount = totals.discrepancy === 0 ? '0.00' :
      formatCurrency(Math.abs(totals.discrepancy), true) // Changed from false to true
    csvRows.push(`"${discrepancyLabel}:","${discrepancyAmount}"`)

    if (totals.hasCashCount && totals.cashCountDate) {
      csvRows.push(`"Last Cash Count Date:","${totals.cashCountDate}"`)
    } else {
      csvRows.push('"Last Cash Count Date:","No cash count available"')
    }

    csvRows.push(`"Checker:","${totals.checker || 'N/A'}"`)
    csvRows.push(`"Export Date:","${dayjs().format('MMM DD, YYYY HH:mm')}"`)

    // Create the CSV content
    const csvContent = csvRows.join('\n')

    // Create a download link
    const link = document.createElement('a')
    link.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent))
    link.setAttribute('download', filename)
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error('Error generating CSV:', error)
    throw new Error('Failed to generate CSV file')
  }
}

/**
 * Export transactions as PDF with enhanced metrics - FIXED VERSION
 * @param {Array} data - Array of transaction objects
 * @param {Object} totals - Enhanced totals object with cash count data
 * @param {string} title - Title for the PDF
 */
export const exportTransactionsAsPDF = (data, totals, title) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.error('Invalid data format for PDF export')
    return
  }

  try {
    // Get column names from the first object
    const columns = Object.keys(data[0])

    // Create table HTML
    let tableHTML = `
      <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { text-align: center; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th, td { padding: 8px; border: 1px solid #ddd; text-align: left; }
          th { background-color: #f2f2f2; }
          .cash-out { color: #d9363e; text-align: right; }
          .cash-in { color: #52c41a; text-align: right; }
          .balance { text-align: right; }
          .summary-section { margin-top: 30px; }
          .summary-table { width: 60%; margin: 0 auto; }
          .summary-table th { background-color: #e6f4ff; text-align: left; font-weight: bold; }
          .summary-table td { text-align: right; font-weight: bold; }
          .total-cash-out { color: #d9363e; }
          .total-cash-in { color: #52c41a; }
          .running-balance { color: ${totals.runningBalance < 0 ? '#d9363e' : '#52c41a'}; }
          .system-cash { color: #1890ff; }
          .actual-cash { color: #722ed1; }
          .surplus { color: #52c41a; }
          .discrepancy { color: #d9363e; }
          .balanced { color: #595959; }
          .number-column { text-align: right; }
          .print-button { 
            padding: 8px 16px;
            background-color: #1890ff;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            margin-top: 20px;
            display: block;
            margin-left: auto;
            margin-right: auto;
          }
          .wrap-text {
            word-wrap: break-word;
            white-space: normal;
            max-width: 95px;
          }
          .export-info {
            text-align: center;
            margin-bottom: 20px;
            color: #666;
            font-size: 12px;
          }
          @media print {
            .print-button { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <div class="export-info">
          Export Date: ${dayjs().format('MMM DD, YYYY HH:mm')}
        </div>
        
        <table>
          <thead>
            <tr>
    `

    // Add column headers
    columns.forEach(column => {
      tableHTML += `<th>${column}</th>`
    })

    tableHTML += `
            </tr>
          </thead>
          <tbody>
    `

    // Add data rows
    data.forEach(row => {
      tableHTML += '<tr>'
      columns.forEach(column => {
        if (column === 'Cash Out' && row[column]) {
          tableHTML += `<td class="cash-out">${row[column] || ''}</td>`
        } else if (column === 'Cash In' && row[column]) {
          tableHTML += `<td class="cash-in">${row[column] || ''}</td>`
        } else if (column === 'Balance') {
          tableHTML += `<td class="balance">${row[column] || ''}</td>`
        } else if (['Sr. No.', 'Cash Out', 'Cash In', 'Balance'].includes(column)) {
          tableHTML += `<td class="number-column">${row[column] || ''}</td>`
        } else if (column === 'Description') {
          tableHTML += `<td class="wrap-text">${row[column] || ''}</td>`
        } else if (column === 'Type') {
          tableHTML += `<td class="wrap-text">${row[column] || ''}</td>`
        } else if (column === 'Invoice Number') {
          tableHTML += `<td class="wrap-text">${row[column] || ''}</td>`
        } else {
          tableHTML += `<td>${row[column] || ''}</td>`
        }
      })
      tableHTML += '</tr>'
    })

    // Determine discrepancy styling and label
    const discrepancyLabel = totals.discrepancy > 0 ? 'Surplus' :
      totals.discrepancy < 0 ? 'Discrepancy' : 'Balanced'
    const discrepancyClass = totals.discrepancy > 0 ? 'surplus' :
      totals.discrepancy < 0 ? 'discrepancy' : 'balanced'
    const discrepancyAmount = totals.discrepancy === 0 ? '0.00' :
      formatCurrency(Math.abs(totals.discrepancy), true) // Changed from false to true

    tableHTML += `
          </tbody>
        </table>
        
        <div class="summary-section">
          <h2 style="text-align: center; margin-bottom: 20px;">Summary</h2>
          <table class="summary-table">
            <tr>
              <th>Metric</th>
              <th>Amount</th>
            </tr>
            <tr>
              <td>Total Cash Out:</td>
              <td class="total-cash-out">${formatCurrency(totals.cashOut, true)}</td>
            </tr>
            <tr>
              <td>Total Cash In:</td>
              <td class="total-cash-in">${formatCurrency(totals.cashIn, true)}</td>
            </tr>
            <tr>
              <td>Running Balance:</td>
              <td class="running-balance">${formatCurrency(totals.runningBalance, true)}</td>
            </tr>
            <tr>
              <td>System Cash on Hand:</td>
              <td class="system-cash">${formatCurrency(totals.systemCashOnHand, true)}</td>
            </tr>
            <tr>
              <td>Actual Cash on Hand:</td>
              <td class="actual-cash">${formatCurrency(totals.actualCashOnHand, true)}</td>
            </tr>
            <tr>
              <td>${discrepancyLabel}:</td>
              <td class="${discrepancyClass}">${discrepancyAmount}</td>
            </tr>
            <tr>
              <td>Last Cash Count Date:</td>
              <td>${totals.hasCashCount && totals.cashCountDate ? totals.cashCountDate : 'No cash count available'}</td>
            </tr>
            <tr>
              <td>Checker:</td>
              <td>${totals.checker || 'N/A'}</td>
            </tr>
          </table>
        </div>
        
        <button class="print-button" onclick="window.print()">Print PDF</button>
        
        <script>
          // Auto-print then close the print dialog when the page loads
          window.onload = function() {
            // Wait for a moment to make sure styles are applied
            setTimeout(() => {
              window.print()
            }, 500)
          }
        </script>
      </body>
      </html>
    `

    // Open a new window with the HTML content
    const printWindow = window.open('', '_blank')
    printWindow.document.write(tableHTML)
    printWindow.document.close()

  } catch (error) {
    console.error('Error generating PDF:', error)
    throw new Error('Failed to generate PDF file')
  }
}

/**
 * Calculate transaction totals (cash in, cash out, running balance)
 * @param {Array} transactions - Array of transaction objects
 * @returns {Object} Object containing totals
 */
export const calculateTransactionTotals = (transactions) => {
  return transactions.reduce((totals, transaction) => {
    return {
      cashIn: totals.cashIn + (transaction.cashIn || 0),
      cashOut: totals.cashOut + (transaction.cashOut || 0),
      runningBalance: transaction.runningBalance // Last transaction's running balance
    }
  }, { cashIn: 0, cashOut: 0, runningBalance: 0 })
}

/**
 * Sort transactions by date
 * @param {Array} transactions - Array of transaction objects
 * @returns {Array} Sorted array of transactions
 */
export const sortTransactionsByDate = (transactions) => {
  return [...transactions].sort((a, b) => {
    const dateA = new Date(a.date + ' ' + a.time)
    const dateB = new Date(b.date + ' ' + b.time)
    return dateB - dateA // Sort in descending order (newest first)
  })
}

/**
 * Filter transactions by date range
 * @param {Array} transactions - Array of transaction objects
 * @param {Date} startDate - Start date for filtering
 * @param {Date} endDate - End date for filtering
 * @returns {Array} Filtered array of transactions
 */
export const filterTransactionsByDateRange = (transactions, startDate, endDate) => {
  if (!startDate || !endDate) return transactions

  return transactions.filter(transaction => {
    const transactionDate = new Date(transaction.date)
    return transactionDate >= startDate && transactionDate <= endDate
  })
}

/**
 * Group transactions by a specific property
 * @param {Array} transactions - Array of transaction objects
 * @param {string} property - Property to group by (e.g., 'expenseType', 'approver')
 * @returns {Object} Object with groups of transactions
 */
export const groupTransactionsByProperty = (transactions, property) => {
  return transactions.reduce((groups, transaction) => {
    const value = transaction[property]
    if (!groups[value]) {
      groups[value] = []
    }
    groups[value].push(transaction)
    return groups
  }, {})
}

/**
 * Get unique values from an array of objects for a specific property
 * @param {Array} array - Array of objects
 * @param {string} property - Property to get unique values for
 * @returns {Array} Array of unique values
 */
export const getUniqueValues = (array, property) => {
  return [...new Set(array.map(item => item[property]))].filter(Boolean)
}

/**
 * Format a date range for display
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return ''

  return `${formatDate(startDate)} - ${formatDate(endDate)}`
}

/**
 * Get enhanced column search props for Ant Design Table with custom filter
 * @param {string} dataIndex - The data index to filter
 * @param {string} placeholder - Placeholder text for filter input
 * @returns {Object} Object containing filter dropdown JSX and other props
 */
export const getEnhancedColumnSearchProps = (dataIndex, placeholder = 'Search') => {
  return {
    filterDropdown: () => (
      <div style={{ padding: 8 }}>
        {/* Filter implementation to be provided in component */}
        <input
          placeholder={placeholder}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      return record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : false
    }
  }
}


export const exportChartsAsPDF = (pieCanvas, barCanvas, title, filename, pieData = [], barData = []) => {
  const pdf = new jsPDF('portrait')
  pdf.setFontSize(16)
  pdf.text(title, 20, 20)

  let yPos = 30

  // === Pie Chart Section ===
  if (pieCanvas) {
    const pieImgData = pieCanvas.toDataURL('image/png')
    pdf.text('Cash Flow Breakdown', 20, yPos)
    yPos += 10
    pdf.addImage(pieImgData, 'PNG', 20, yPos, 170, 100)
    yPos += 110

    //  Add values below the chart
    pdf.setFontSize(12)
    pieData.forEach((item, i) => {
      pdf.text(`${item.label}: ${item.value}`, 25, yPos + (i * 8))
    })
    yPos += pieData.length * 8 + 15
  }

  // === Bar Chart Section ===
  if (barCanvas) {
    const barImgData = barCanvas.toDataURL('image/png')
    pdf.setFontSize(16)
    pdf.text('Expenses by Type', 20, yPos)
    yPos += 10
    pdf.addImage(barImgData, 'PNG', 20, yPos, 170, 100)
    yPos += 110

    //  Add values below the chart
    pdf.setFontSize(12)
    barData.forEach((item, i) => {
      pdf.text(`${item.label}: ${item.value}`, 25, yPos + (i * 8))
    })
  }

  // Open Preview
  window.open(pdf.output('bloburl'))


}

